// deps
import React, { memo, useCallback } from "react";

// internal-deps
import { DeliverySpeedDetail, IStressComms, MROChildCart, Option } from "@swiggy-private/cart-types";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { MRO_CART_DELIVERY_SPEED_TEST_IDS } from "@swiggy-private/cart-test-ids";

// helpers
import { getMROChildCartRestaurantName } from "@cart-ui/helpers/Mro";

// components
import { DeliverySpeedOption } from "../../delivery-options";
import RestaurantImage from "./RestaurantImage";

// styles
import {
    ButtonWrapper,
    CartHalfCardHeaderWrapper,
    HalfCardSeparatorWrapper,
    ImageWrapper,
    NameWrapper,
} from "../styles";

// constants
import {
    HALF_CARD_RX_IMAGE_SIZE,
    MRO_CART_DELIVERY_SPEED_OPTION_ID_PREFIX,
    MRO_CONFIRM_BUTTON_TEXT,
} from "@cart-ui/constants/MROCart";

interface DeliveryHalfCardProps {
    cart: MROChildCart;
    deliverySpeedWidgetInfoV2: {
        options: Option[];
        optInText?: string;
        slaComms?: IStressComms;
    };
    onDeliverySpeedItemClick: (item: DeliverySpeedDetail, fromHalfCard?: boolean, text?: string) => void;
    selectedSlaOption: DeliverySpeedDetail | undefined;
    onOptInWidgetClick: (optInValue: boolean) => void;
    deliveryTypeOptIn?: boolean;
    currentSla: Option | undefined;
    isHalfCardOpen: boolean;
    setCurrentSla: React.Dispatch<React.SetStateAction<Option | undefined>>;
    setIsHalfCardOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const NOOP = () => undefined;

const DeliveryHalfCard = ({
    deliverySpeedWidgetInfoV2,
    selectedSlaOption,
    onDeliverySpeedItemClick,
    onOptInWidgetClick,
    deliveryTypeOptIn,
    cart,
    currentSla,
    isHalfCardOpen,
    setCurrentSla,
    setIsHalfCardOpen,
}: DeliveryHalfCardProps) => {
    const pendingSelectedType = currentSla?.slaData?.type ?? selectedSlaOption?.type;
    const rxName = getMROChildCartRestaurantName(cart);
    const closeHalfCard = useCallback(() => {
        setIsHalfCardOpen(false);
    }, []);

    const onOptionChange = useCallback((option: Option) => {
        setCurrentSla(option);
    }, []);

    const onConfirm = useCallback(() => {
        if (currentSla?.slaData) {
            onDeliverySpeedItemClick(currentSla.slaData as DeliverySpeedDetail);
        }
        setIsHalfCardOpen(false);
    }, [currentSla, onDeliverySpeedItemClick, setIsHalfCardOpen]);

    return (
        <>
            <HalfCard
                isOpen={isHalfCardOpen}
                onClose={closeHalfCard}
                variant="v3"
                hasCustomPadding
                data-testid={MRO_CART_DELIVERY_SPEED_TEST_IDS.HALF_CARD}
            >
                <CartHalfCardHeaderWrapper>
                    <ImageWrapper height={HALF_CARD_RX_IMAGE_SIZE.height} width={HALF_CARD_RX_IMAGE_SIZE.width}>
                        <RestaurantImage
                            cart={cart}
                            height={HALF_CARD_RX_IMAGE_SIZE.height}
                            width={HALF_CARD_RX_IMAGE_SIZE.width}
                            alt={rxName}
                        />
                    </ImageWrapper>
                    <NameWrapper>
                        <Text variant="V2.body1.neutral.bold" color="text_High_Emphasis" maxLines={1}>
                            {rxName}
                        </Text>
                    </NameWrapper>
                </CartHalfCardHeaderWrapper>
                <HalfCardSeparatorWrapper />
                <div>
                    <DeliverySpeedOption
                        options={deliverySpeedWidgetInfoV2.options}
                        onChange={onOptionChange}
                        idPrefix={MRO_CART_DELIVERY_SPEED_OPTION_ID_PREFIX}
                        selectedSlaOption={pendingSelectedType}
                        slaComms={deliverySpeedWidgetInfoV2.slaComms}
                        optInText={deliverySpeedWidgetInfoV2.optInText}
                        onOptInWidgetClick={onOptInWidgetClick}
                        deliveryTypeOptIn={deliveryTypeOptIn}
                        onSlaCommsViewEnter={NOOP} // no need here and can't make optional as changes needed in the dls
                    />
                </div>
                <ButtonWrapper>
                    <FullWidthButton
                        variant="positive"
                        onClick={onConfirm}
                        data-testid={MRO_CART_DELIVERY_SPEED_TEST_IDS.CONFIRM}
                    >
                        {MRO_CONFIRM_BUTTON_TEXT}
                    </FullWidthButton>
                </ButtonWrapper>
            </HalfCard>
        </>
    );
};

export default memo(DeliveryHalfCard);
